import { useConvexMutation } from "@convex-dev/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { ConvexError } from "convex/values";
import { Controller, useForm } from "react-hook-form";
import { Pressable, Text, View } from "react-native";
import Modal from "react-native-modal";
import Toast from "react-native-toast-message";
import { z } from "zod";
import CloseIcon from "@/assets/svgs/close-icon";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { Button } from "@/elements/components/ui/button";
import { Textarea } from "@/elements/components/ui/textarea";
import { api } from "@/elements/components/utils/convex-api";
import StarRatingPicker from "./star-rating-picker";

const TutorReviewFormSchema = z.object({
  rating: z.number().min(1, "Please select a rating").max(5),
  comment: z.string().trim().min(1, "Please add a comment"),
});

interface TutorReviewModalProps {
  isVisible: boolean;
  tutorId: Id<"tutorProfiles">;
  studentId: Id<"students">;
  tutorName: string;
  onClose: () => void;
  onSubmitted: () => void;
}

function TutorReviewModal({
  isVisible,
  tutorId,
  studentId,
  tutorName,
  onClose,
  onSubmitted,
}: TutorReviewModalProps) {
  const { control, handleSubmit, setValue, watch, formState, reset } = useForm({
    defaultValues: {
      rating: 0,
      comment: "",
    },
    resolver: zodResolver(TutorReviewFormSchema),
    mode: "onChange",
  });

  const rating = watch("rating");

  const { mutateAsync: submitReview, isPending } = useMutation({
    mutationFn: useConvexMutation(api.parent.tutor.mutations.submitTutorReview),
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await submitReview({
        tutorId,
        studentId,
        rating: data.rating,
        comment: data.comment,
      });

      reset();
      Toast.show({
        type: "info",
        text1: "Thanks for your review!",
        text2: "It will appear on the profile once approved.",
      });
      onSubmitted();
      onClose();
    } catch (error) {
      Toast.show({
        type: "error",
        text1:
          error instanceof ConvexError
            ? String(error.data)
            : "Failed to submit review",
      });
    }
  });

  return (
    <Modal
      animationIn="slideInUp"
      animationOut="slideOutDown"
      isVisible={isVisible}
      onBackdropPress={onClose}
      useNativeDriver
      useNativeDriverForBackdrop
      hideModalContentWhileAnimating
    >
      <ResponsiveContent>
        <View className="mx-4 rounded-xl bg-teachLoop-creamBase px-8 py-10">
          <View className="items-end">
            <Pressable
              onPress={onClose}
              className="active:opacity-80"
              style={{ marginHorizontal: -12 }}
            >
              <CloseIcon
                pathClassName="stroke-teachLoop-textPrimary"
                className="size-6"
              />
            </Pressable>
          </View>

          <View className="gap-6">
            <View className="gap-2">
              <Text className="font-geologica-medium text-xl text-black">
                Review {tutorName}
              </Text>
              <Text className="font-geologica-regular text-sm text-dark">
                Share your experience. Your review will be visible after our
                team approves it.
              </Text>
            </View>

            <View className="items-center gap-2">
              <StarRatingPicker
                value={rating}
                onChange={(value) =>
                  setValue("rating", value, { shouldValidate: true })
                }
              />
              {formState.errors.rating && (
                <Text className="font-geologica-regular text-sm text-red-600">
                  {formState.errors.rating.message}
                </Text>
              )}
            </View>

            <Controller
              control={control}
              name="comment"
              render={({ field: { onChange, value } }) => (
                <Textarea
                  placeholder="Tell other parents about your experience..."
                  value={value}
                  onChangeText={onChange}
                  size="md"
                  multiline
                  numberOfLines={4}
                  textAlignVertical="top"
                />
              )}
            />
            {formState.errors.comment && (
              <Text className="-mt-4 font-geologica-regular text-sm text-red-600">
                {formState.errors.comment.message}
              </Text>
            )}

            <Button
              size="md"
              intent="primary"
              className="w-full"
              disabled={!formState.isValid || isPending}
              onPress={onSubmit}
            >
              <Button.Text size="md">
                {isPending ? "Submitting..." : "Submit review"}
              </Button.Text>
            </Button>
          </View>
        </View>
      </ResponsiveContent>
    </Modal>
  );
}

export default TutorReviewModal;
